import React from 'react';
import { caseStudies } from '../data/images';
import { ArrowRight, CheckCircle, AlertTriangle, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { trackCTAClick } from '../utils/analytics';

const CaseStudy = () => {
  const scrollToConsultation = () => {
    trackCTAClick('Schedule a Strategy Session', 'case_study');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="case-studies" className="py-16 sm:py-24 bg-slate-950" data-testid="case-study-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Proven Track Record
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Proven Results in
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              Real Organizations
            </span>
          </h2>
        </div>

        {/* Case Studies */}
        <div className="space-y-12 sm:space-y-16">
          {caseStudies.map((cs, i) => (
            <div
              key={i}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? 'lg:direction-rtl' : ''}`}
              data-testid={`case-study-${i}`}
            >
              {/* Image */}
              <div className={`${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={cs.image}
                    alt={cs.title}
                    loading="lazy"
                    className="w-full aspect-[16/10] object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-white text-xs font-semibold border border-white/20">
                      {cs.industry}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-6">{cs.title}</h3>

                {/* Before */}
                <div className="mb-5 p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-400" />
                    <span className="text-red-400 text-xs font-semibold uppercase tracking-wider">Before</span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{cs.before}</p>
                </div>

                {/* What We Did */}
                <div className="mb-5 p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-4 w-4 text-cyan-400" />
                    <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wider">What We Did</span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{cs.approach}</p>
                </div>

                {/* Results */}
                <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">Results</span>
                  </div>
                  <ul className="space-y-2">
                    {cs.results.map((r, j) => (
                      <li key={j} className="flex items-start gap-2 text-slate-300 text-sm">
                        <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 sm:mt-16">
          <Button
            onClick={scrollToConsultation}
            data-testid="case-study-cta"
            size="lg"
            className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 py-5 text-base transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
          >
            Schedule a Strategy Session
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CaseStudy;
